import {
	deleteUserAccount,
	deleteUserForCompany,
	getUserAuthStatuses,
	inviteUserToCompany,
	sendUserResetPasswordEmail,
	setUserAccountEnabled,
	type User,
	updateUserForCompany,
} from "@repo/ui";
import {
	KeyRound,
	Loader2,
	Pencil,
	Plus,
	Search,
	Trash2,
	UserCheck,
	UserX,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";

interface UsersPageProps {
	onNavigateToUser: (userId: string) => void;
	createForCompanyId?: string | null;
	onCreateDialogClose?: () => void;
}

type EditableRole = "user" | "admin";

interface GroupedUser {
	id: string;
	email: string;
	displayName: string;
	phoneNumbers: string[];
	memberships: User[];
	companyNames: string[];
	isSystemAdmin: boolean;
	status: "enabled" | "disabled" | "loading";
	lastLogin: Date | string | null;
}

interface MembershipFormRow {
	key: string;
	companyId: string;
	displayName: string;
	phoneNumber: string;
	role: User["role"];
	existing: boolean;
	originalCompanyId?: string;
}

const EMPTY_ADD_FORM = {
	companyId: "",
	email: "",
	displayName: "",
	phoneNumber: "",
	role: "user" as EditableRole,
};

function getUserLastLogin(user: User): Date | string | null {
	return ((user as User & { lastLoginAt?: Date | string | null }).lastLoginAt ??
		user.lastLogin ??
		null) as Date | string | null;
}

function normalizeValue(value: string | undefined) {
	return (value || "").trim();
}

export function UsersPage({
	onNavigateToUser,
	createForCompanyId,
	onCreateDialogClose,
}: UsersPageProps) {
	const { users, companies, agents, testRouting } = useData();
	const [searchTerm, setSearchTerm] = useState("");
	const [isCreatingUser, setIsCreatingUser] = useState(false);
	const [isSavingUser, setIsSavingUser] = useState(false);
	const [isTogglingUser, setIsTogglingUser] = useState(false);
	const [isDeletingUser, setIsDeletingUser] = useState(false);
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(
		Boolean(createForCompanyId),
	);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState<GroupedUser | null>(null);
	const [formData, setFormData] = useState({
		...EMPTY_ADD_FORM,
		companyId: createForCompanyId ?? "",
	});
	const [membershipRows, setMembershipRows] = useState<MembershipFormRow[]>([]);
	const [authStatuses, setAuthStatuses] = useState<
		Record<string, { disabled: boolean }>
	>({});

	useEffect(() => {
		const userIds = Array.from(
			new Set(users.map((user) => user.id).filter(Boolean) as string[]),
		);
		if (userIds.length === 0) {
			setAuthStatuses({});
			return;
		}

		let cancelled = false;
		getUserAuthStatuses({ userIds })
			.then(({ data }) => {
				if (!cancelled) setAuthStatuses(data.statuses);
			})
			.catch((error) => {
				console.error("Failed to load Firebase Auth user statuses", error);
				if (!cancelled) {
					setAuthStatuses({});
					toast.error("Failed to load user account statuses");
				}
			});

		return () => {
			cancelled = true;
		};
	}, [users]);

	const companiesById = useMemo(
		() => new Map(companies.map((company) => [company.id!, company])),
		[companies],
	);

	const agentsById = useMemo(
		() => new Map(agents.map((agent) => [agent.id, agent])),
		[agents],
	);

	const testRoutingByPhone = useMemo(
		() => new Map(testRouting.map((r) => [r.phoneNumber, r])),
		[testRouting],
	);

	const groupedUsers = useMemo((): GroupedUser[] => {
		const grouped = new Map<string, User[]>();

		for (const user of users) {
			const key = user.id || user.email;
			if (!key) continue;
			const existing = grouped.get(key) || [];
			existing.push(user);
			grouped.set(key, existing);
		}

		return Array.from(grouped.entries())
			.map(([id, memberships]) => {
				const sortedMemberships = [...memberships].sort((a, b) => {
					const companyA = companiesById.get(a.companyId)?.name || a.companyId;
					const companyB = companiesById.get(b.companyId)?.name || b.companyId;
					return companyA.localeCompare(companyB);
				});
				const firstMembership = sortedMemberships[0];
				const phoneNumbers = Array.from(
					new Set(
						sortedMemberships
							.map((membership) => normalizeValue(membership.phoneNumber))
							.filter(Boolean),
					),
				);
				const lastLogin = sortedMemberships.reduce<Date | string | null>(
					(latest, membership) => {
						const candidate = getUserLastLogin(membership);
						if (!candidate) return latest;
						if (!latest) return candidate;
						return new Date(candidate) > new Date(latest) ? candidate : latest;
					},
					null,
				);

				return {
					id,
					email: firstMembership?.email || "",
					displayName:
						sortedMemberships.find((membership) =>
							normalizeValue(membership.displayName),
						)?.displayName || "",
					phoneNumbers,
					memberships: sortedMemberships,
					companyNames: sortedMemberships.map(
						(membership) =>
							companiesById.get(membership.companyId)?.name ||
							membership.companyId,
					),
					isSystemAdmin: sortedMemberships.some(
						(membership) => membership.role === "system-admin",
					),
					status: authStatuses[id]
						? authStatuses[id].disabled
							? ("disabled" as const)
							: ("enabled" as const)
						: ("loading" as const),
					lastLogin,
				};
			})
			.sort((a, b) => a.email.localeCompare(b.email));
	}, [authStatuses, companiesById, users]);

	const filteredUsers = useMemo(() => {
		if (!searchTerm) return groupedUsers;

		const search = searchTerm.toLowerCase();
		return groupedUsers.filter((user) => {
			const haystacks = [
				user.email,
				user.displayName,
				...user.companyNames,
				...user.phoneNumbers,
				...user.memberships.map((membership) => membership.role),
				user.isSystemAdmin ? "system-admin" : "",
			];
			return haystacks.some((value) => value?.toLowerCase().includes(search));
		});
	}, [groupedUsers, searchTerm]);

	const formatTimestamp = (timestamp: string | Date | null | undefined) => {
		if (!timestamp) return "N/A";
		return new Date(timestamp).toLocaleString();
	};

	const buildMembershipRows = (user: GroupedUser): MembershipFormRow[] =>
		user.memberships.map((membership) => ({
			key: `${membership.companyId}-${membership.id}`,
			companyId: membership.companyId,
			displayName: membership.displayName || "",
			phoneNumber: membership.phoneNumber || "",
			role: membership.role,
			existing: true,
			originalCompanyId: membership.companyId,
		}));

	const handleAddSubmit = async () => {
		if (
			!(createForCompanyId ?? formData.companyId) ||
			!formData.email ||
			!formData.role ||
			!formData.displayName
		) {
			toast.error("Please fill in all fields");
			return;
		}

		setIsCreatingUser(true);
		const companyName =
			companiesById.get(formData.companyId)?.name || formData.companyId;

		try {
			const phoneNumber = normalizeValue(formData.phoneNumber);
			await inviteUserToCompany({
				email: formData.email.trim(),
				displayName: formData.displayName.trim(),
				companyId: createForCompanyId ?? formData.companyId,
				...(phoneNumber ? { phoneNumber } : {}),
				role: formData.role,
			});

			toast.success(
				`Invitation sent to "${formData.email}" for ${companyName}`,
			);

			setFormData(EMPTY_ADD_FORM);
			setIsAddDialogOpen(false);
			onCreateDialogClose?.();
		} catch (error) {
			console.error("Failed to create user", error);
			toast.error(
				`Failed to create user: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		} finally {
			setIsCreatingUser(false);
		}
	};

	const handleEditClick = (user: GroupedUser) => {
		setSelectedUser(user);
		setMembershipRows(buildMembershipRows(user));
		setIsEditDialogOpen(true);
	};

	const handleMembershipChange = (
		key: string,
		field: keyof MembershipFormRow,
		value: string,
	) => {
		setMembershipRows((rows) =>
			rows.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
		);
	};

	const handleAddMembershipRow = () => {
		setMembershipRows((rows) => [
			...rows,
			{
				key: `new-${Date.now()}-${rows.length}`,
				companyId: "",
				displayName: selectedUser?.displayName || "",
				phoneNumber: selectedUser?.phoneNumbers[0] || "",
				role: "user",
				existing: false,
			},
		]);
	};

	const handleRemoveMembershipRow = (key: string) => {
		setMembershipRows((rows) => rows.filter((row) => row.key !== key));
	};

	const handleEditSubmit = async () => {
		if (!selectedUser) return;

		const trimmedRows = membershipRows.map((row) => ({
			...row,
			displayName: normalizeValue(row.displayName),
			phoneNumber: normalizeValue(row.phoneNumber),
		}));

		if (trimmedRows.length === 0) {
			toast.error("A user must belong to at least one company");
			return;
		}

		if (
			trimmedRows.some((row) => !row.companyId || !row.displayName || !row.role)
		) {
			toast.error("Please complete every membership row");
			return;
		}

		const companyIds = trimmedRows.map((row) => row.companyId);
		if (new Set(companyIds).size !== companyIds.length) {
			toast.error("Each company can only appear once");
			return;
		}

		setIsSavingUser(true);

		try {
			const originalRows = new Map(
				selectedUser.memberships.map((membership) => [
					membership.companyId,
					membership,
				]),
			);

			for (const membership of selectedUser.memberships) {
				if (
					!trimmedRows.some((row) => row.companyId === membership.companyId)
				) {
					await deleteUserForCompany({
						userId: selectedUser.id,
						companyId: membership.companyId,
					});
				}
			}

			for (const row of trimmedRows) {
				const originalMembership = row.originalCompanyId
					? originalRows.get(row.originalCompanyId)
					: undefined;

				if (!row.existing) {
					await inviteUserToCompany({
						email: selectedUser.email,
						displayName: row.displayName,
						companyId: row.companyId,
						role: row.role as EditableRole,
					});

					if (row.phoneNumber) {
						await updateUserForCompany({
							userId: selectedUser.id,
							companyId: row.companyId,
							updates: {
								displayName: row.displayName,
								phoneNumber: row.phoneNumber,
								role: row.role === "system-admin" ? "admin" : row.role,
							},
						});
					}
					continue;
				}

				if (!originalMembership) continue;

				const updates: Partial<
					Pick<User, "displayName" | "phoneNumber" | "role">
				> = {};

				if (
					row.displayName !== normalizeValue(originalMembership.displayName)
				) {
					updates.displayName = row.displayName;
				}

				if (
					row.phoneNumber !== normalizeValue(originalMembership.phoneNumber)
				) {
					updates.phoneNumber = row.phoneNumber;
				}

				if (
					row.role !== "system-admin" &&
					row.role !== originalMembership.role
				) {
					updates.role = row.role;
				}

				if (Object.keys(updates).length > 0) {
					await updateUserForCompany({
						userId: selectedUser.id,
						companyId: row.companyId,
						updates,
					});
				}
			}

			toast.success(`User "${selectedUser.email}" updated successfully`);
			setIsEditDialogOpen(false);
			setSelectedUser(null);
		} catch (error) {
			console.error("Failed to update user", error);
			toast.error(
				`Failed to update user: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		} finally {
			setIsSavingUser(false);
		}
	};

	const handleResetPassword = async () => {
		if (!selectedUser) return;

		try {
			await sendUserResetPasswordEmail(selectedUser.email);
			toast.success(`Password reset email sent to "${selectedUser.email}"`);
		} catch (error) {
			toast.error(
				`Failed to send password reset email: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
			return;
		}
	};

	const handleToggleEnabled = async () => {
		if (!selectedUser) return;
		if (selectedUser.status === "loading") return;
		const nextEnabled = selectedUser.status !== "enabled";
		const action = nextEnabled ? "enabled" : "disabled";

		setIsTogglingUser(true);
		try {
			await setUserAccountEnabled({
				userId: selectedUser.id,
				enabled: nextEnabled,
			});
			setAuthStatuses((statuses) => ({
				...statuses,
				[selectedUser.id]: { disabled: !nextEnabled },
			}));
			toast.success(`User "${selectedUser.email}" ${action} successfully`);
			setIsEditDialogOpen(false);
			setSelectedUser(null);
		} catch (error) {
			console.error(`Failed to ${action} user`, error);
			toast.error(`Failed to ${action} user`);
		} finally {
			setIsTogglingUser(false);
		}
	};

	const handleDeleteClick = (user: GroupedUser) => {
		setSelectedUser(user);
		setIsDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!selectedUser) return;

		setIsDeletingUser(true);
		try {
			await deleteUserAccount({ userId: selectedUser.id });

			toast.success(`User "${selectedUser.email}" deleted successfully`);
			setIsDeleteDialogOpen(false);
			setSelectedUser(null);
		} catch (error) {
			console.error("Failed to delete user", error);
			toast.error(
				`Failed to delete user: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		} finally {
			setIsDeletingUser(false);
		}
	};

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-start">
				<div>
					<h1>Users</h1>
					<p className="text-slate-600">
						Manage user accounts across all companies
					</p>
				</div>
				<Dialog
					open={isAddDialogOpen}
					onOpenChange={(open) => {
						setIsAddDialogOpen(open);
						if (!open) onCreateDialogClose?.();
					}}
				>
					<DialogTrigger asChild>
						<Button>
							<Plus className="mr-2 h-4 w-4" />
							Add User
						</Button>
					</DialogTrigger>
					<DialogContent className="admin-editor-dialog admin-editor-dialog--compact">
						<DialogHeader>
							<DialogTitle>Add New User</DialogTitle>
							<DialogDescription>
								Create a new user account. Phone number is optional.
							</DialogDescription>
						</DialogHeader>
						<div className="admin-editor-body admin-editor-grid">
							<div className="space-y-2">
								<Label htmlFor="user-company">Company</Label>
								<Select
									disabled={Boolean(createForCompanyId)}
									value={formData.companyId}
									onValueChange={(value) =>
										setFormData({ ...formData, companyId: value })
									}
								>
									<SelectTrigger id="user-company">
										<SelectValue placeholder="Select a company" />
									</SelectTrigger>
									<SelectContent>
										{companies.map((company) => (
											<SelectItem key={company.id} value={company.id!}>
												{company.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label htmlFor="user-name">Name</Label>
								<Input
									id="user-name"
									placeholder="John Doe"
									value={formData.displayName}
									onChange={(e) =>
										setFormData({ ...formData, displayName: e.target.value })
									}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="user-email">Email</Label>
								<Input
									id="user-email"
									type="email"
									placeholder="user@example.com"
									value={formData.email}
									onChange={(e) =>
										setFormData({ ...formData, email: e.target.value })
									}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="user-phone">Phone Number (optional)</Label>
								<Input
									id="user-phone"
									type="tel"
									placeholder="+14155552671"
									value={formData.phoneNumber}
									onChange={(e) =>
										setFormData({ ...formData, phoneNumber: e.target.value })
									}
									dir="ltr"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="user-role">Role</Label>
								<Select
									value={formData.role}
									onValueChange={(value: EditableRole) =>
										setFormData({ ...formData, role: value })
									}
								>
									<SelectTrigger id="user-role">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="user">User</SelectItem>
										<SelectItem value="admin">Admin</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setIsAddDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button onClick={handleAddSubmit} disabled={isCreatingUser}>
								{isCreatingUser && (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								)}
								Create User
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			<Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
				<DialogContent className="admin-editor-dialog admin-editor-dialog--workspace">
					<DialogHeader>
						<DialogTitle>Edit User</DialogTitle>
						<DialogDescription>
							Manage company memberships, role per company, phone number, and
							display name. Email cannot be changed.
						</DialogDescription>
					</DialogHeader>

					<div className="admin-editor-body space-y-4">
						<div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
							<div className="min-w-0 flex-1 space-y-1">
								<Label htmlFor="edit-email">Email</Label>
								<Input
									id="edit-email"
									type="email"
									value={selectedUser?.email || ""}
									disabled
									className="w-full min-w-0 bg-slate-100 cursor-not-allowed"
								/>
							</div>
							<div className="flex items-center gap-2 self-end">
								{selectedUser?.isSystemAdmin && <Badge>system-admin</Badge>}
								<Button variant="outline" onClick={handleAddMembershipRow}>
									<Plus className="mr-2 h-4 w-4" />
									Add Company
								</Button>
							</div>
						</div>

						<div className="overflow-x-auto rounded-md border">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Company</TableHead>
										<TableHead>Display Name</TableHead>
										<TableHead>Phone Number</TableHead>
										<TableHead>Role</TableHead>
										<TableHead className="w-12"></TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{membershipRows.map((row) => {
										const companyOptions = companies.filter((company) => {
											if (company.id === row.companyId) return true;
											return !membershipRows.some(
												(otherRow) =>
													otherRow.key !== row.key &&
													otherRow.companyId === company.id,
											);
										});

										return (
											<TableRow key={row.key}>
												<TableCell className="align-top">
													<Select
														disabled={row.existing}
														value={row.companyId}
														onValueChange={(value) =>
															handleMembershipChange(
																row.key,
																"companyId",
																value,
															)
														}
													>
														<SelectTrigger>
															<SelectValue placeholder="Select company" />
														</SelectTrigger>
														<SelectContent>
															{companyOptions.map((company) => (
																<SelectItem
																	key={company.id}
																	value={company.id!}
																>
																	{company.name}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</TableCell>
												<TableCell className="align-top">
													<Input
														value={row.displayName}
														onChange={(e) =>
															handleMembershipChange(
																row.key,
																"displayName",
																e.target.value,
															)
														}
														placeholder="Display name"
													/>
												</TableCell>
												<TableCell className="align-top">
													<Input
														value={row.phoneNumber}
														onChange={(e) =>
															handleMembershipChange(
																row.key,
																"phoneNumber",
																e.target.value,
															)
														}
														placeholder="+1 555 123 4567"
													/>
												</TableCell>
												<TableCell className="align-top">
													{row.role === "system-admin" ? (
														<Badge>system-admin</Badge>
													) : (
														<Select
															value={row.role}
															onValueChange={(value: EditableRole) =>
																handleMembershipChange(row.key, "role", value)
															}
														>
															<SelectTrigger>
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																<SelectItem value="user">User</SelectItem>
																<SelectItem value="admin">Admin</SelectItem>
															</SelectContent>
														</Select>
													)}
												</TableCell>
												<TableCell className="align-top text-right">
													<Button
														variant="ghost"
														size="sm"
														onClick={() => handleRemoveMembershipRow(row.key)}
													>
														<Trash2 className="h-4 w-4 text-red-600" />
													</Button>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</div>

						<div className="pt-4 border-t space-y-3">
							<Button
								variant="outline"
								className="w-full"
								onClick={handleResetPassword}
							>
								<KeyRound className="mr-2 h-4 w-4" />
								Reset Password
							</Button>

							<Button
								variant="outline"
								className="w-full"
								onClick={handleToggleEnabled}
								disabled={isTogglingUser || selectedUser?.status === "loading"}
							>
								{isTogglingUser && (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								)}
								{selectedUser?.status === "enabled" ? (
									<>
										<UserX className="mr-2 h-4 w-4" />
										Disable Account
									</>
								) : (
									<>
										<UserCheck className="mr-2 h-4 w-4" />
										Enable Account
									</>
								)}
							</Button>

							<Button
								variant="outline"
								className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
								onClick={() => {
									setIsEditDialogOpen(false);
									if (selectedUser) handleDeleteClick(selectedUser);
								}}
							>
								<Trash2 className="mr-2 h-4 w-4" />
								Delete User
							</Button>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsEditDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button onClick={handleEditSubmit} disabled={isSavingUser}>
							{isSavingUser && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							Save Changes
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete User?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete user "{selectedUser?.email}" from
							all assigned companies? This action cannot be undone and will
							remove all associated memberships.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeletingUser}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={(event) => {
								event.preventDefault();
								void handleDeleteConfirm();
							}}
							disabled={isDeletingUser}
							className="bg-red-600 hover:bg-red-700"
						>
							{isDeletingUser && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<Card>
				<CardHeader>
					<CardTitle>All Users</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="mb-4">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
							<Input
								placeholder="Search by email, company, phone, or role..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10"
							/>
						</div>
					</div>

					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Companies</TableHead>
								<TableHead>Name</TableHead>
								<TableHead>Email</TableHead>
								<TableHead>Phone Numbers</TableHead>
								<TableHead>Test Agent</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Last Login</TableHead>
								<TableHead className="w-12"></TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredUsers.map((user) => (
								<TableRow
									key={user.id}
									className="cursor-pointer hover:bg-slate-50"
									onClick={() => onNavigateToUser(user.id)}
								>
									<TableCell>
										<div className="flex flex-wrap gap-2">
											{user.memberships.map((membership) => {
												const companyName =
													companiesById.get(membership.companyId)?.name ||
													membership.companyId;
												const isCompanyAdmin =
													membership.role === "admin" ||
													membership.role === "system-admin";

												return (
													<Badge
														key={`${user.id}-${membership.companyId}`}
														variant={isCompanyAdmin ? "default" : "outline"}
													>
														{companyName}
													</Badge>
												);
											})}
										</div>
									</TableCell>
									<TableCell>
										<div className="space-y-1">
											<div>{user.displayName || "N/A"}</div>
											{user.isSystemAdmin && (
												<Badge className="w-fit">system-admin</Badge>
											)}
										</div>
									</TableCell>
									<TableCell>{user.email}</TableCell>
									<TableCell>
										{user.phoneNumbers.length > 0 ? (
											<div className="flex flex-wrap gap-2">
												{user.phoneNumbers.map((phoneNumber) => (
													<Badge
														key={`${user.id}-${phoneNumber}`}
														variant="outline"
													>
														{phoneNumber}
													</Badge>
												))}
											</div>
										) : (
											<span className="text-slate-500">N/A</span>
										)}
									</TableCell>
									<TableCell>
										<div className="flex flex-wrap gap-2">
											{user.phoneNumbers.map((phoneNumber) => {
												const routing = testRoutingByPhone.get(phoneNumber);
												if (!routing) return null;
												const agent = agentsById.get(routing.agentId);
												return (
													<Badge
														key={`${user.id}-${phoneNumber}-routing`}
														variant="secondary"
													>
														{agent?.name || routing.agentId}
													</Badge>
												);
											})}
										</div>
									</TableCell>
									<TableCell>
										{user.status === "enabled" ? (
											<Badge className="bg-green-100 text-green-800">
												Enabled
											</Badge>
										) : user.status === "disabled" ? (
											<Badge variant="outline" className="text-slate-600">
												Disabled
											</Badge>
										) : (
											<Badge variant="secondary">Loading…</Badge>
										)}
									</TableCell>
									<TableCell>{formatTimestamp(user.lastLogin)}</TableCell>
									<TableCell className="text-right">
										<div className="flex justify-end gap-2">
											<Button
												variant="ghost"
												size="sm"
												onClick={(e: React.MouseEvent) => {
													e.stopPropagation();
													handleEditClick(user);
												}}
											>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="sm"
												onClick={(e: React.MouseEvent) => {
													e.stopPropagation();
													handleDeleteClick(user);
												}}
											>
												<Trash2 className="h-4 w-4 text-red-600" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					{filteredUsers.length === 0 && (
						<div className="text-center py-8 text-slate-500">
							No users found
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
